/*
|--------------------------------------------------------------------------
| Utility: deleteHelper.js
| Author: Sayed Ahmed
| Description: Provides helper functions for deleting single or multiple
|              entities with SweetAlert confirmation and Redux dispatching.
|--------------------------------------------------------------------------
*/

import swal from "sweetalert";

/**
 * Handles single entity deletion with confirmation dialog.
 *
 * @param {Object} params
 * @param {number|string} params.id - The ID of the entity to delete.
 * @param {string} [params.entityName="item"] - Entity display name.
 * @param {Function} params.deleteFunc - Redux thunk or action creator.
 * @param {Function} params.dispatch - Redux dispatch function.
 * @param {Function} [params.refetchFunc] - Optional function to re-fetch the data after deletion.
 * @param {Function} [params.t] - Translation function (defaults to identity).
 */
export async function handleDeleteEntity({
  id,
  entityName = "item",
  deleteFunc,
  dispatch,
  refetchFunc,
  t = (txt) => txt,
}) {
  const confirmTitle = t(`Confirm ${entityName} Deletion`);
  const confirmText = t(`Once deleted, this ${entityName.toLowerCase()} cannot be recovered.`);

  const willDelete = await swal({
    title: confirmTitle,
    text: confirmText,
    icon: "warning",
    buttons: true,
    dangerMode: true,
  });

  if (willDelete) {
    const result = await dispatch(deleteFunc(id));
    if (result.payload?.success) {
      if (refetchFunc) dispatch(refetchFunc());
    }
  }
}

/**
 * Handles bulk deletion of multiple entities with confirmation dialog.
 *
 * @param {Object} params
 * @param {Array<number|string>} params.ids - Array of IDs to delete.
 * @param {string} [params.entityName="items"] - Entity plural name.
 * @param {Function} params.deleteFunc - Redux thunk or action creator.
 * @param {Function} params.dispatch - Redux dispatch function.
 * @param {Function} [params.refetchFunc] - Optional function to re-fetch data.
 * @param {Function} [params.t] - Translation function.
 * @param {Function} [params.onSuccess] - Callback after successful deletion.
 */
export async function handleBulkDeleteEntities({
  ids = [],
  entityName = "items",
  deleteFunc,
  dispatch,
  refetchFunc,
  t = (txt) => txt,
  onSuccess = () => {},
}) {
  if (ids.length === 0) return;

  const confirmTitle = t(`Confirm ${entityName} Deletion`);
  const confirmText = t(`Once deleted, these ${entityName.toLowerCase()} cannot be recovered.`);

  const willDelete = await swal({
    title: confirmTitle,
    text: confirmText,
    icon: "warning",
    buttons: true,
    dangerMode: true,
  });

  if (willDelete) {
    const result = await dispatch(deleteFunc({ data: { ids } }));
    if (result.payload?.success) {
      if (refetchFunc) dispatch(refetchFunc());
      onSuccess(result.payload);
    }
  }
}
